import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchMeals, fetchCategories, fetchMealTypes,
    selectAllMeals, selectAllCategories, selectAllMealTypes,
    invalidateMeals
} from '../redux/adminSlice';
import { Pencil, Trash2, Eye, Plus, Search, X } from 'lucide-react';
import DataTable from 'react-data-table-component';
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const initialFormData = {
    mealName: '',
    description: '',
    foodType: '', // veg, non-veg, or vegan
    category: '',
    categoryId: '', // Store the category ID
    mealType: '',
    mealTypeId: '', // Store the meal type ID
    package: [],
    image: [], // Will store image URLs instead of base64
    fareDetails: {
        totalFare: '',
        strikeOff: '',
        discount: ''
    },
    portionDetails: {
        portion: 1,
        weight: '',
        servingSize: '',
        caloriesPerPortion: '',
        proteinPerPortion: '',
        fatPerPortion: '',
        carbsPerPortion: ''
    },
    moreDetails: {
        energy: '',
        protein: '',
        fat: '',
        carbohydrates: '',
        allergens: [] // Initialize as an empty array
    }
};

export default function MealPage() {
    const dispatch = useDispatch();
    const mealPackages = useSelector(selectAllMeals);
    const categoryPackage = useSelector(selectAllCategories);
    const mealType = useSelector(selectAllMealTypes);
    const [isCanvasOpen, setIsCanvasOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [formData, setFormData] = useState(initialFormData);
    const [selectedMealId, setSelectedMealId] = useState(null);
    const [imageFiles, setImageFiles] = useState([]);
    const [imagePreviewUrls, setImagePreviewUrls] = useState([]); // For preview only
    const [selectedMealTypes, setSelectedMealTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    // Fetches removed in favor of Redux

    useEffect(() => {
        dispatch(fetchMeals());
        dispatch(fetchCategories());
        dispatch(fetchMealTypes());
    }, [dispatch]);

    // Cleanup object URLs when component unmounts
    useEffect(() => {
        return () => {
            imagePreviewUrls.forEach(url => {
                URL.revokeObjectURL(url);
            });
        };
    }, [imagePreviewUrls]);

    // Add the missing isMealSelected function
    const isMealSelected = (mealTypeId) => {
        return selectedMealTypes.includes(mealTypeId);
    };

    // Handle selection of meal types
    const handleMealTypeSelection = (e) => {
        const typeId = e.target.value;
        const isChecked = e.target.checked;

        if (isChecked) {
            setSelectedMealTypes(prev => [...prev, typeId]);
        } else {
            setSelectedMealTypes(prev => prev.filter(id => id !== typeId));
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCategoryChange = (e) => {
        const selectedIndex = e.target.selectedIndex;
        const selectedOption = e.target.options[selectedIndex];
        const categoryId = selectedOption.getAttribute('data-id') || '';
        const categoryName = e.target.value;

        setFormData(prev => ({
            ...prev,
            category: categoryName,
            categoryId: categoryId
        }));
    };

    const handleMealTypeChange = (e) => {
        const selectedIndex = e.target.selectedIndex;
        const selectedOption = e.target.options[selectedIndex];
        const mealTypeId = selectedOption.getAttribute('data-id') || '';
        const mealTypeName = e.target.value;

        setFormData(prev => ({
            ...prev,
            mealType: mealTypeName,
            mealTypeId: mealTypeId
        }));
    };

    const handleFareChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            fareDetails: {
                ...prev.fareDetails,
                [name]: value
            }
        }));
    };

    const handlePortionChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            portionDetails: {
                ...prev.portionDetails,
                [name]: name === 'portion' ? parseInt(value, 10) || 0 : value
            }
        }));
    };

    const handleMoreDetailsChange = (e) => {
        const { name, value } = e.target;
        if (name === 'allergens') {
            // Convert comma-separated string to array
            const allergensArray = value.split(',').map(item => item.trim());
            setFormData(prev => ({
                ...prev,
                moreDetails: {
                    ...prev.moreDetails,
                    [name]: allergensArray
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                moreDetails: {
                    ...prev.moreDetails,
                    [name]: value
                }
            }));
        }
    };

    const handleImageUpload = (event) => {
        const files = Array.from(event.target.files);
        handleFiles(files);
    };

    const handleFiles = (files) => {
        // Filter for image files only
        const imageFiles = files.filter(file => file.type.startsWith('image/'));

        // Limit to 5 files
        const limitedFiles = imageFiles.slice(0, 5);

        setImageFiles(limitedFiles);

        // Create temporary preview URLs for display in the form
        const previewUrls = limitedFiles.map(file => URL.createObjectURL(file));
        setImagePreviewUrls(previewUrls);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const files = Array.from(e.dataTransfer.files);
        handleFiles(files);
    };

    // const handleImageChange = (e) => {
    //     const files = Array.from(e.target.files);
    //     setImageFiles(files);

    //     // Create temporary preview URLs for display in the form
    //     const previewUrls = files.map(file => URL.createObjectURL(file));
    //     setImagePreviewUrls(previewUrls);

    //     // When editing, keep existing URLs and add new files for upload
    //     // The actual image URLs will be set by the server after upload
    //     if (selectedMealId) {
    //         // Keep existing image URLs when editing
    //         // New files will be uploaded separately
    //     } else {
    //         // For new entries, clear existing URLs
    //         setFormData(prev => ({
    //             ...prev,
    //             image: [] // The actual URLs will be set by the server after upload
    //         }));
    //     }
    // };

    const handleRemoveImage = (index) => {
        // Clean up the object URL to prevent memory leaks
        if (imagePreviewUrls[index]) {
            URL.revokeObjectURL(imagePreviewUrls[index]);
        }

        // Remove image from preview and from files to upload
        setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
        setImageFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleRemoveExistingImage = (index) => {
        // Remove existing image from formData
        setFormData(prev => ({
            ...prev,
            image: prev.image.filter((_, i) => i !== index)
        }));
    };

    const handleAdd = () => {
        // Clean up existing object URLs
        imagePreviewUrls.forEach(url => {
            URL.revokeObjectURL(url);
        });

        setFormData(initialFormData);
        setSelectedMealId(null);
        setImageFiles([]);
        setImagePreviewUrls([]);
        setSelectedMealTypes([]);
        setIsCanvasOpen(true);
    };

    const handleEdit = (meal) => {
        console.log(meal.identifier, 'meal');

        // Get the category name from the categoryPackage using the category ID
        const categoryId = Array.isArray(meal.category) ? meal.category[0] : meal.category;
        const categoryObj = categoryPackage.find(cat => cat._id === categoryId || cat.identifier === categoryId);
        const categoryName = categoryObj ? categoryObj.categoryName : '';

        // Set selected meal types from mealTypeDetails (API returns populated details)
        if (meal.mealTypeDetails && Array.isArray(meal.mealTypeDetails) && meal.mealTypeDetails.length > 0) {
            // mealTypeDetails has { mealType: "Breakfast", identifier: "wKf8T" }
            // We need to find the _id in the mealType state by matching identifier
            const selectedIds = meal.mealTypeDetails
                .map(detail => {
                    const found = mealType.find(t =>
                        t.identifier === detail.identifier ||
                        t.mealType?.toLowerCase() === detail.mealType?.toLowerCase()
                    );
                    return found ? found._id : null;
                })
                .filter(Boolean); // remove nulls
            setSelectedMealTypes(selectedIds);
        } else if (meal.mealTypes && Array.isArray(meal.mealTypes)) {
            setSelectedMealTypes(meal.mealTypes.map(type => type._id || type));
        } else if (Array.isArray(meal.mealType) && meal.mealType.length > 0) {
            setSelectedMealTypes(meal.mealType.map(id => id));
        } else {
            setSelectedMealTypes([]);
        }

        // Ensure portionDetails has all required fields with defaults if not present
        const portionDetails = meal.portionDetails || {};
        const completePortionDetails = {
            portion: portionDetails.portion || 1,
            weight: portionDetails.weight || '',
            servingSize: portionDetails.servingSize || '',
            caloriesPerPortion: portionDetails.caloriesPerPortion || '',
            proteinPerPortion: portionDetails.proteinPerPortion || '',
            fatPerPortion: portionDetails.fatPerPortion || '',
            carbsPerPortion: portionDetails.carbsPerPortion || ''
        };

        setSelectedMealId(meal._id);
        setFormData({
            mealName: meal.mealName || '',
            description: meal.description || '',
            foodType: meal.foodType || '', // Ensure foodType is loaded when editing
            identifier: meal.identifier || '', // <-- Added identifier field
            category: categoryName,
            categoryId: categoryId || '',
            mealType: '', // Since mealType is handled through selectedMealTypes
            mealTypeId: '',
            package: Array.isArray(meal.package) ? meal.package : [],
            image: Array.isArray(meal.image) ? meal.image : [],
            fareDetails: {
                totalFare: meal.fareDetails?.totalFare || '',
                strikeOff: meal.fareDetails?.strikeOff || '',
                discount: meal.fareDetails?.discount || ''
            },
            portionDetails: completePortionDetails,
            moreDetails: {
                energy: meal.moreDetails?.energy || '',
                protein: meal.moreDetails?.protein || '',
                fat: meal.moreDetails?.fat || '',
                carbohydrates: meal.moreDetails?.carbohydrates || '',
                allergens: Array.isArray(meal.moreDetails?.allergens)
                    ? meal.moreDetails.allergens
                    : meal.moreDetails?.allergens ? meal.moreDetails.allergens.split(',').map(item => item.trim()) : []
            }
        });

        // Clean up existing object URLs
        imagePreviewUrls.forEach(url => {
            URL.revokeObjectURL(url);
        });

        setImageFiles([]);
        setImagePreviewUrls([]);
        setIsCanvasOpen(true);
    };


    const handleDelete = async (id) => {
        try {
            await axios.delete(`https://api.dailyfit.ae/api/admin/delete`, {
                data: { identifier: id },
                withCredentials: true,
            });
            dispatch(invalidateMeals());
            dispatch(fetchMeals());

            // setMealPackages(mealPackages.filter(pkg => pkg.id !== id));
            toast.success("Category deleted successfully!");
        } catch (error) {
            console.error("Error deleting category:", error);
            toast.error("Failed to delete category. Please try again.");
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Create a clean form data object
            const formDataToSend = new FormData();

            // 1. Add basic fields
            formDataToSend.append('mealName', formData.mealName || '');
            formDataToSend.append('description', formData.description || '');
            formDataToSend.append('foodType', formData.foodType || 'veg'); // Added foodType

            // 2. Add meal types (as separate entries with same key)
            const mealTypes = [];
            if (Array.isArray(selectedMealTypes) && selectedMealTypes.length > 0) {
                selectedMealTypes.forEach(typeId => {
                    const foundType = mealType.find(type => type._id === typeId);
                    if (foundType?.identifier) {
                        mealTypes.push(foundType.identifier);
                    }
                });
            }

            // Add meal types to form data (only if we have any)
            if (mealTypes.length > 0) {
                mealTypes.forEach(type => formDataToSend.append('mealType', type));
            } else {
                formDataToSend.append('mealType', '');
            }

            // 3. Add fareDetails as individual fields
            formDataToSend.append('fareDetails[totalFare]', formData.fareDetails?.totalFare || '1');
            formDataToSend.append('fareDetails[discount]', formData.fareDetails?.discount || '1');
            formDataToSend.append('fareDetails[strikeOff]', formData.fareDetails?.strikeOff || '1');

            // 4. Add moreDetails as individual fields
            formDataToSend.append('moreDetails[energy]', formData.moreDetails?.energy || '200');
            formDataToSend.append('moreDetails[protein]', formData.moreDetails?.protein || '300');
            formDataToSend.append('moreDetails[fat]', formData.moreDetails?.fat || '250');
            formDataToSend.append('moreDetails[carbohydrates]', formData.moreDetails?.carbohydrates || '230');

            // Add allergens array
            const allergens = Array.isArray(formData.moreDetails?.allergens) &&
                formData.moreDetails.allergens.length > 0 ?
                formData.moreDetails.allergens : ['NO'];
            allergens.forEach(allergen => {
                formDataToSend.append('moreDetails[allergens][]', allergen);
            });

            // 5. Add portion details
            formDataToSend.append('portionDetails[portion]', formData.portionDetails?.portion || 1);
            formDataToSend.append('portionDetails[weight]', formData.portionDetails?.weight || '');
            formDataToSend.append('portionDetails[servingSize]', formData.portionDetails?.servingSize || '');
            formDataToSend.append('portionDetails[caloriesPerPortion]', formData.portionDetails?.caloriesPerPortion || '');
            formDataToSend.append('portionDetails[proteinPerPortion]', formData.portionDetails?.proteinPerPortion || '');
            formDataToSend.append('portionDetails[fatPerPortion]', formData.portionDetails?.fatPerPortion || '');
            formDataToSend.append('portionDetails[carbsPerPortion]', formData.portionDetails?.carbsPerPortion || '');

            // 5. Handle images
            // Add existing image URLs if editing
            if (selectedMealId && Array.isArray(formData.image)) {
                formData.image.forEach((url, index) => {
                    if (typeof url === 'string' && url.trim() !== '') {
                        formDataToSend.append('existingImages[]', url);
                    }
                });
            }

            // Add new image files with proper handling
            if (Array.isArray(imageFiles) && imageFiles.length > 0) {
                // Append each image file to FormData with 'files' field name
                imageFiles.forEach((file) => {
                    if (file instanceof File) {
                        formDataToSend.append('files', file);
                    }
                });
            }

            // Log the form data before sending
            console.log('=== Form Data Being Sent to API ===');
            for (let [key, value] of formDataToSend.entries()) {
                console.log(`${key}:`, value instanceof File ? 'File' : value);
            }

            let response;
            if (selectedMealId) {
                // Add the meal identifier to the form data for updates
                if (formData.identifier) {
                    formDataToSend.append('identifier', formData.identifier);
                } else if (selectedMealId) {
                    formDataToSend.append('identifier', selectedMealId);
                }

                // Update existing meal
                console.log('Updating existing meal with ID:', selectedMealId);
                response = await axios.patch('https://api.dailyfit.ae/api/admin/update-meal', formDataToSend, {
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
            } else {
                // Add new meal
                console.log('Adding new meal...');
                response = await axios.post('https://api.dailyfit.ae/api/admin/add-meal', formDataToSend, {
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
            }

            // ✅ Show success toast from response message
            toast.success(response?.data?.message || "Meal saved successfully");

            // ✅ Reset form
            dispatch(invalidateMeals());
            await dispatch(fetchMeals());

            // Clean up object URLs
            imagePreviewUrls.forEach(url => {
                URL.revokeObjectURL(url);
            });

            setIsCanvasOpen(false);
            setFormData(initialFormData);
            setImageFiles([]);
            setImagePreviewUrls([]);
            setSelectedMealId(null);
            setSelectedMealTypes([]);

        } catch (error) {
            console.error('Error saving meal:', error);
            toast.error(error?.response?.data?.message || "Failed to save meal");
        } finally {
            setLoading(false); // Hide loader
        }
    };


    const columns = [
        { name: 'Name', selector: row => row.mealName, sortable: true },
        { name: 'Description', selector: row => row.description, sortable: true },
        {
            name: 'Category',
            selector: row => {
                // Handle if category is an array or string
                if (Array.isArray(row.category)) {
                    // Try to find category names from the IDs
                    const categoryNames = row.category.map(catId => {
                        const cat = categoryPackage.find(c => c._id === catId || c.identifier === catId);
                        return cat ? cat.categoryName : catId;
                    });
                    return categoryNames.join(', ');
                }
                return row.category;
            },
            sortable: true
        },
        {
            name: 'Food Type',
            selector: row => row.foodType,
            cell: row => {
                const colors = {
                    'veg': 'bg-green-100 text-green-800 border-green-200',
                    'non-veg': 'bg-red-100 text-red-800 border-red-200',
                    'vegan': 'bg-yellow-100 text-yellow-800 border-yellow-200'
                };
                const colorClass = colors[row.foodType?.toLowerCase()] || 'bg-gray-100 text-gray-800 border-gray-200';
                return (
                    <span className={`px-2 py-1 rounded-full text-xs font-bold border uppercase tracking-wider ${colorClass}`}>
                        {row.foodType || 'N/A'}
                    </span>
                );
            },
            sortable: true
        },
        {
            name: 'Type',
            selector: row => {
                if (Array.isArray(row.mealType) && row.mealType.length > 0) {
                    const typeNames = row.mealType.map(typeId => {
                        const type = mealType.find(t => t._id === typeId);
                        return type ? type.mealType : typeId;
                    });
                    return typeNames.join(', ');
                }
                return row.mealType || 'N/A';
            },
            sortable: true
        },
        {
            name: 'Price',
            selector: row => {
                const fare = row.fareDetails?.totalFare;
                return fare ? `AED ${fare.toFixed(2)}` : 'N/A';
            },
            sortable: true
        },
        {
            name: 'Actions',
            cell: (row) => (
                <div className="flex justify-center space-x-2">
                    <button onClick={() => handleEdit(row)} className="bg-yellow-500 text-white p-2 rounded hover:bg-yellow-600">
                        <Pencil size={16} />
                    </button>
                    <button onClick={() => handleDelete(row.identifier)} className="bg-red-500 text-white p-2 rounded hover:bg-red-600">
                        <Trash2 size={16} />
                    </button>
                </div>
            ),
        },
    ];

    const filteredPackages = useMemo(() => {
        return Array.isArray(mealPackages) ? mealPackages.filter(pkg =>
            (pkg.mealName?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
            (pkg.description?.toLowerCase() || "").includes(searchTerm.toLowerCase())
        ) : [];
    }, [mealPackages, searchTerm]);

    return (
        <div className="p-4">
            {loading && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
                    <div className="flex flex-col items-center justify-center">
                        <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-b-4 border-green-500 mb-6"></div>
                        <div className="text-white text-2xl font-bold tracking-wide mb-2">Loading Meals...</div>
                        <div className="text-green-200 text-sm">Please wait while we fetch the latest meal data.</div>
                    </div>
                </div>
            )}

            {/* <div className="">
                <button onClick={handleAdd} className="flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600">
                    <Plus size={16} />
                    <span>Add Meal</span>
                </button>
            </div> */}

            <div className="w-full h-20  flex justify-end items-center ">
                <button
                    onClick={handleAdd}
                    className="flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
                >
                    <Plus size={16} />
                    <span>Add Meal </span>
                </button>
            </div>

            <div className="bg-white rounded-lg shadow-md w-full max-w-[1200px]">
                <div className="p-4 border-b">
                    <div className="flex justify-end items-center space-x-4">
                        <div className="relative w-64">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                            <input
                                type="text"
                                placeholder="Search meal..."
                                className="pl-8 pr-4 py-2 w-full border rounded-md"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div className="p-4">
                    <DataTable columns={columns} data={filteredPackages} pagination responsive highlightOnHover />
                </div>
            </div>

            {isCanvasOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-50">
                    <ToastContainer position="top-right" />
                    <div className="bg-white w-1/3 p-6 h-full overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">{selectedMealId ? 'Edit Meal' : 'Add Meal - UPDATED v2'}</h2>
                            <button onClick={() => setIsCanvasOpen(false)} className="bg-gray-500 text-white p-2 rounded hover:bg-gray-600">
                                <X size={16} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">Meal Name</label>
                                <input
                                    type="text"
                                    name="mealName"
                                    placeholder="Meal Name"
                                    value={formData.mealName}
                                    onChange={handleChange}
                                    className="border p-2 w-full rounded mt-1"
                                    required
                                />
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">Food Type</label>
                                <select
                                    name="foodType"
                                    value={formData.foodType}
                                    onChange={handleChange}
                                    className="border p-2 w-full rounded mt-1"
                                    required
                                >
                                    <option value="">Select Food Type</option>
                                    <option value="veg">Veg</option>
                                    <option value="non-veg">Non-Veg</option>
                                    <option value="vegan">Vegan</option>
                                </select>
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">Description</label>
                                <textarea
                                    name="description"
                                    placeholder="Description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    className="border p-2 w-full rounded mt-1 min-h-20"
                                    required
                                ></textarea>
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">Category</label>
                                <select
                                    name="category"
                                    value={formData.category}
                                    onChange={handleCategoryChange}
                                    className="border p-2 w-full rounded mt-1"
                                    required
                                >
                                    <option value="">Select Category</option>
                                    {categoryPackage.map((category) => (
                                        <option
                                            key={category.identifier || category._id}
                                            value={category.categoryName}
                                            data-id={category._id || category.identifier}
                                        >
                                            {category.categoryName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="border p-2 rounded max-h-64 overflow-y-auto">
                                <label className="block text-sm font-medium text-gray-700">Select Meal Type</label>
                                {mealType && mealType.length > 0 ? (
                                    mealType.map((type) => (
                                        <div key={type._id} className="flex items-center mb-2 p-2 hover:bg-gray-100 rounded">
                                            <input
                                                type="checkbox"
                                                id={`type-${type._id}`}
                                                name="types"
                                                value={type._id}
                                                checked={isMealSelected(type._id)}
                                                onChange={handleMealTypeSelection}
                                                className="mr-2 h-4 w-4"
                                            />
                                            <label htmlFor={`type-${type._id}`} className="flex flex-col">
                                                <span className="font-medium">{type.mealType}</span>
                                            </label>
                                        </div>
                                    ))
                                ) : (
                                    <p>No meal types available</p>
                                )}
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">Images (Max 5 files)</label>
                                <div
                                    className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-indigo-400 transition-colors"
                                    onDragOver={handleDragOver}
                                    onDrop={handleDrop}
                                >
                                    <div className="space-y-1 text-center">
                                        <svg
                                            className="mx-auto h-12 w-12 text-gray-400"
                                            stroke="currentColor"
                                            fill="none"
                                            viewBox="0 0 48 48"
                                            aria-hidden="true"
                                        >
                                            <path
                                                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                                strokeWidth={2}
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                        <div className="flex text-sm text-gray-600">
                                            <label
                                                htmlFor="file-upload"
                                                className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
                                            >
                                                <span>Upload files</span>
                                                <input
                                                    id="file-upload"
                                                    name="file-upload"
                                                    type="file"
                                                    multiple
                                                    accept="image/*"
                                                    onChange={handleImageUpload}
                                                    className="sr-only"
                                                />
                                            </label>
                                            <p className="pl-1">or drag and drop</p>
                                        </div>
                                        <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB each</p>
                                    </div>
                                </div>

                                {/* File list */}
                                {imageFiles.length > 0 && (
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500 mb-1">Selected Files:</p>
                                        <div className="space-y-2">
                                            {imageFiles.map((file, index) => (
                                                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                                    <div className="flex items-center space-x-2">
                                                        <span className="text-sm text-gray-700">{file.name}</span>
                                                        <span className="text-xs text-gray-500">
                                                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                                        </span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveImage(index)}
                                                        className="text-red-500 hover:text-red-700"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Existing images */}
                                {formData.image && formData.image.length > 0 && (
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500 mb-1">Existing Images:</p>
                                        <div className="flex flex-wrap gap-2">
                                            {formData.image.map((img, index) => (
                                                <div key={`existing-${index}`} className="relative">
                                                    <img
                                                        src={img}
                                                        alt={`Image ${index + 1}`}
                                                        className="h-20 w-20 object-cover rounded"
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = "/placeholder-image.jpg";
                                                        }}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveExistingImage(index)}
                                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                                                    >
                                                        <X size={12} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* New image previews */}
                                {imagePreviewUrls.length > 0 && (
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500 mb-1">New Image Previews:</p>
                                        <div className="flex flex-wrap gap-2">
                                            {imagePreviewUrls.map((preview, index) => (
                                                <div key={`preview-${index}`} className="relative">
                                                    <img
                                                        src={preview}
                                                        alt={`Preview ${index + 1}`}
                                                        className="h-20 w-20 object-cover rounded"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="mb-4">
                                <h3 className="font-bold mb-2">Fare Details</h3>
                                <div className="grid grid-cols-3 gap-2">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Total Fare</label>
                                        <input
                                            type="number"
                                            name="totalFare"
                                            placeholder="Total Fare"
                                            value={formData.fareDetails.totalFare}
                                            onChange={handleFareChange}
                                            className="border p-2 w-full rounded mt-1"
                                            required
                                            step="0.01"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Strike Off</label>
                                        <input
                                            type="number"
                                            name="strikeOff"
                                            placeholder="Strike Off Price"
                                            value={formData.fareDetails.strikeOff}
                                            onChange={handleFareChange}
                                            className="border p-2 w-full rounded mt-1"
                                            step="0.01"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Discount</label>
                                        <input
                                            type="number"
                                            name="discount"
                                            placeholder="Discount"
                                            value={formData.fareDetails.discount}
                                            onChange={handleFareChange}
                                            className="border p-2 w-full rounded mt-1"
                                            step="0.01"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Portion Details Section */}
                            <div className="mb-4 border-t pt-4">
                                <h3 className="font-bold mb-3">Portion Details</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm text-gray-600 mb-1">Number of Portions</label>
                                        <input
                                            type="number"
                                            name="portion"
                                            placeholder="Portion"
                                            value={formData.portionDetails.portion}
                                            onChange={handlePortionChange}
                                            className="border p-2 w-full rounded mt-1"
                                            min="1"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Weight (g)</label>
                                        <input
                                            type="number"
                                            name="weight"
                                            placeholder="Weight in grams"
                                            value={formData.portionDetails.weight}
                                            onChange={handlePortionChange}
                                            className="border p-2 w-full rounded mt-1"
                                            min="0"
                                            step="0.1"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Serving Size</label>
                                        <input
                                            type="text"
                                            name="servingSize"
                                            placeholder="e.g., 1 breast, 1 cup"
                                            value={formData.portionDetails.servingSize}
                                            onChange={handlePortionChange}
                                            className="border p-2 w-full rounded mt-1"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Calories per Portion</label>
                                        <input
                                            type="number"
                                            name="caloriesPerPortion"
                                            placeholder="Calories"
                                            value={formData.portionDetails.caloriesPerPortion}
                                            onChange={handlePortionChange}
                                            className="border p-2 w-full rounded mt-1"
                                            min="0"
                                            step="1"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Protein per Portion (g)</label>
                                        <input
                                            type="number"
                                            name="proteinPerPortion"
                                            placeholder="Protein in grams"
                                            value={formData.portionDetails.proteinPerPortion}
                                            onChange={handlePortionChange}
                                            className="border p-2 w-full rounded mt-1"
                                            min="0"
                                            step="0.1"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Fat per Portion (g)</label>
                                        <input
                                            type="number"
                                            name="fatPerPortion"
                                            placeholder="Fat in grams"
                                            value={formData.portionDetails.fatPerPortion}
                                            onChange={handlePortionChange}
                                            className="border p-2 w-full rounded mt-1"
                                            min="0"
                                            step="0.1"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Carbs per Portion (g)</label>
                                        <input
                                            type="number"
                                            name="carbsPerPortion"
                                            placeholder="Carbs in grams"
                                            value={formData.portionDetails.carbsPerPortion}
                                            onChange={handlePortionChange}
                                            className="border p-2 w-full rounded mt-1"
                                            min="0"
                                            step="0.1"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="mb-4">
                                <h3 className="font-bold mb-2">Nutritional Details</h3>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Energy</label>
                                        <input
                                            type="number"
                                            name="energy"
                                            placeholder="Energy"
                                            value={formData.moreDetails.energy}
                                            onChange={handleMoreDetailsChange}
                                            className="border p-2 w-full rounded mt-1"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Protein</label>
                                        <input
                                            type="number"
                                            name="protein"
                                            placeholder="Protein"
                                            value={formData.moreDetails.protein}
                                            onChange={handleMoreDetailsChange}
                                            className="border p-2 w-full rounded mt-1"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Fat</label>
                                        <input
                                            type="number"
                                            name="fat"
                                            placeholder="Fat"
                                            value={formData.moreDetails.fat}
                                            onChange={handleMoreDetailsChange}
                                            className="border p-2 w-full rounded mt-1"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Carbohydrates</label>
                                        <input
                                            type="number"
                                            name="carbohydrates"
                                            placeholder="Carbohydrates"
                                            value={formData.moreDetails.carbohydrates}
                                            onChange={handleMoreDetailsChange}
                                            className="border p-2 w-full rounded mt-1"
                                        />
                                    </div>
                                </div>

                                <div className="mt-2">
                                    <label className="block text-sm font-medium text-gray-700">Allergens</label>
                                    <input
                                        type="text"
                                        name="allergens"
                                        placeholder="Allergens (comma-separated)"
                                        value={Array.isArray(formData.moreDetails.allergens) ? formData.moreDetails.allergens.join(', ') : ''}
                                        onChange={handleMoreDetailsChange}
                                        className="border p-2 w-full rounded mt-1"
                                    />
                                </div>
                            </div>

                            <div className="flex space-x-2 mt-6">
                                <button type="submit" className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600">
                                    {selectedMealId ? 'Update' : 'Save'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsCanvasOpen(false)}
                                    className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div >
            )
            }
            <ToastContainer position="top-right" />
        </div >
    );
}